import { applyPinsAfterEdit, migrateDocument, pruneLinks, syncLinksAfterEdit, type ProjectDocument } from '@scs/domain-engine';
import { Api, ApiError } from '../lib/api';
import { clearJournal, writeJournal } from '../lib/journal';

export type SaveState = 'saved' | 'dirty' | 'saving' | 'offline' | 'conflict' | 'error' | 'expired' | 'readonly';

/** A command records the full before/after document; documents are immutable values. */
export interface Command {
  label: string;
  before: ProjectDocument;
  after: ProjectDocument;
}

export interface Selection {
  cabinetIds: string[];
  partId: string | null;
  /** Additional parts (Ctrl+click), e.g. for linking several panels. */
  extraPartIds?: string[];
  /** A shelf set of the (single) selected assembly, chosen in the outliner. */
  shelfSetId?: string | null;
  /** The front group of the (single) selected assembly, chosen in the outliner. */
  frontGroup?: boolean;
  /** A drawer set of the (single) selected assembly, chosen in the outliner. */
  drawerSetId?: string | null;
  /** A wall of the room (and possibly a window / door / socket … in it); no unit is selected then. */
  room?: { lineId: string; itemId?: string | null; pointId?: string | null } | null;
}

/** Keeps a room selection only while its wall (and item) still exist. */
function roomSelection(doc: ProjectDocument, r: Selection['room']): Selection['room'] {
  const plan = doc.roomPlan;
  if (r?.pointId) {
    // A corner: kept while a wall still ends in it.
    const wall = plan?.lines.find((l) => l.kind === 'wall' && (l.a === r.pointId || l.b === r.pointId));
    return wall ? { lineId: wall.id, itemId: null, pointId: r.pointId } : null;
  }
  if (!r || !plan?.lines.some((l) => l.id === r.lineId && l.kind === 'wall')) return null;
  const item = r.itemId ? plan.items.find((i) => i.id === r.itemId) : undefined;
  return item ? { lineId: item.lineId, itemId: item.id } : { lineId: r.lineId, itemId: null };
}

export interface EditorState {
  doc: ProjectDocument;
  /** Server revision the current document is based on. */
  baseRevision: number;
  /** Last document known to be stored on the server at `baseRevision`. */
  serverDoc: ProjectDocument;
  past: Command[];
  future: Command[];
  selection: Selection;
  save: SaveState;
  saveError: string | null;
  conflict: { headRevision: number } | null;
  lastSavedAt: string | null;
}

const AUTOSAVE_MS = 1500;
const MAX_HISTORY = 200;

export class EditorStore {
  private state: EditorState;
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private journalTimer: ReturnType<typeof setTimeout> | null = null;
  private inFlight = false;
  private disposed = true;

  constructor(
    private readonly projectId: string,
    private readonly userId: string,
    doc: ProjectDocument,
    revision: number,
    private readonly readOnly: boolean,
  ) {
    this.state = {
      doc,
      baseRevision: revision,
      serverDoc: doc,
      past: [],
      future: [],
      selection: { cabinetIds: [], partId: null },
      save: readOnly ? 'readonly' : 'saved',
      saveError: null,
      conflict: null,
      lastSavedAt: null,
    };
  }

  /** Lifecycle is attach/detach (not one-shot) so React StrictMode's double effects are harmless. */
  attach() {
    this.disposed = false;
    window.addEventListener('online', this.onOnline);
    if (this.state.doc !== this.state.serverDoc) this.scheduleSave(AUTOSAVE_MS);
    return () => this.detach();
  }

  detach() {
    this.disposed = true;
    window.removeEventListener('online', this.onOnline);
    if (this.timer) clearTimeout(this.timer);
    if (this.journalTimer) clearTimeout(this.journalTimer);
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getState = () => this.state;

  private set(patch: Partial<EditorState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  get canEdit() {
    return !this.readOnly;
  }

  /**
   * Applies a named mutation as an undoable command. `pins: false` skips the pin compensation for
   * edits that place panels themselves (e.g. resizing a whole assembly).
   */
  exec(label: string, mutate: (draft: ProjectDocument) => void, opts?: { pins?: boolean }) {
    if (this.readOnly) return;
    const before = this.state.doc;
    const after = structuredClone(before);
    mutate(after);
    // Linked dimensions follow the edit inside the same command (one undo step).
    pruneLinks(after);
    syncLinksAfterEdit(before, after);
    // Pinned panels/cabinets keep their anchor point when their size changed (incl. via links).
    if (opts?.pins !== false) applyPinsAfterEdit(before, after);
    if (JSON.stringify(after) === JSON.stringify(before)) return;
    const past = [...this.state.past, { label, before, after }].slice(-MAX_HISTORY);
    this.applyDoc(after, { past, future: [] });
  }

  undo() {
    const cmd = this.state.past.at(-1);
    if (!cmd || this.readOnly) return;
    this.applyDoc(cmd.before, { past: this.state.past.slice(0, -1), future: [cmd, ...this.state.future] });
  }

  redo() {
    const [cmd, ...rest] = this.state.future;
    if (!cmd || this.readOnly) return;
    this.applyDoc(cmd.after, { past: [...this.state.past, cmd], future: rest });
  }

  select(selection: Selection) {
    this.set({ selection });
  }

  private applyDoc(doc: ProjectDocument, patch: Partial<EditorState>) {
    const valid = new Set([...doc.cabinets.map((c) => c.id), ...doc.assemblies.map((a) => a.id)]);
    const sel = this.state.selection;
    const selection = {
      cabinetIds: sel.cabinetIds.filter((id) => valid.has(id)),
      partId: sel.partId && valid.has(sel.partId.split('/')[0]!) ? sel.partId : null,
      extraPartIds: (sel.extraPartIds ?? []).filter((p) => valid.has(p.split('/')[0]!)),
      shelfSetId: sel.shelfSetId && doc.assemblies.find((a) => a.id === sel.cabinetIds[0])?.shelfSets?.some((s) => s.id === sel.shelfSetId) ? sel.shelfSetId : null,
      frontGroup: !!sel.frontGroup && !!doc.assemblies.find((a) => a.id === sel.cabinetIds[0])?.fronts?.length,
      drawerSetId: sel.drawerSetId && doc.assemblies.find((a) => a.id === sel.cabinetIds[0])?.drawerSets?.some((s) => s.id === sel.drawerSetId) ? sel.drawerSetId : null,
      room: roomSelection(doc, sel.room),
    };
    const dirty = doc !== this.state.serverDoc;
    const save: SaveState = this.state.save === 'conflict' || this.state.save === 'expired' ? this.state.save : dirty ? (this.state.save === 'offline' ? 'offline' : 'dirty') : 'saved';
    this.set({ ...patch, doc, selection, save });
    this.scheduleJournal();
    if (dirty && save !== 'conflict' && save !== 'expired') this.scheduleSave(AUTOSAVE_MS);
  }

  private scheduleJournal() {
    if (this.journalTimer) clearTimeout(this.journalTimer);
    this.journalTimer = setTimeout(() => {
      const { doc, serverDoc, baseRevision } = this.state;
      if (doc === serverDoc) void clearJournal(this.userId, this.projectId);
      else void writeJournal({ userId: this.userId, projectId: this.projectId, baseRevision, document: doc });
    }, 300);
  }

  private scheduleSave(ms: number) {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.saveNow(), ms);
  }

  private onOnline = () => {
    if (this.state.save === 'offline') this.scheduleSave(200);
  };

  /** Saves the current document. Concurrent edits during a save trigger another save afterwards. */
  async saveNow(): Promise<void> {
    if (this.readOnly || this.disposed) return;
    if (this.inFlight) {
      this.scheduleSave(300);
      return;
    }
    const { doc, serverDoc, baseRevision, save } = this.state;
    if (doc === serverDoc || save === 'conflict' || save === 'expired') return;
    this.inFlight = true;
    this.set({ save: 'saving', saveError: null });
    try {
      const res = await Api.saveDocument(this.projectId, baseRevision, doc);
      const stillCurrent = this.state.doc === doc;
      this.set({
        baseRevision: res.revision,
        serverDoc: doc,
        lastSavedAt: res.savedAt,
        save: stillCurrent ? 'saved' : 'dirty',
      });
      if (stillCurrent) void clearJournal(this.userId, this.projectId);
      else {
        this.scheduleJournal();
        this.scheduleSave(AUTOSAVE_MS);
      }
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 0) {
        this.set({ save: 'offline', saveError: err.message });
        this.scheduleSave(10_000);
      } else if (err.status === 409) {
        this.set({ save: 'conflict', conflict: { headRevision: Number(err.body.headRevision) } });
      } else if (err.status === 401) {
        this.set({ save: 'expired', saveError: err.message });
      } else {
        this.set({ save: 'error', saveError: err.message });
      }
    } finally {
      this.inFlight = false;
    }
  }

  /** Conflict resolution A: discard local state and load the server head (local copy stays in the journal). */
  async loadServerVersion() {
    const p = await Api.project(this.projectId);
    await writeJournal({ userId: this.userId, projectId: this.projectId, baseRevision: this.state.baseRevision, document: this.state.doc });
    const doc = migrateDocument(p.document);
    this.set({ doc, serverDoc: doc, baseRevision: p.revision, past: [], future: [], save: 'saved', conflict: null, saveError: null });
  }

  /** Conflict resolution B: store my document as a separate new project. */
  async saveAsCopy(name: string) {
    const created = await Api.createProject(name, '', this.state.doc);
    return created.id;
  }

  /** Recovery from the local journal (base revision must match the server head). */
  restoreFromJournal(doc: ProjectDocument) {
    this.exec('Vraćanje lokalnih izmjena', (d) => Object.assign(d, structuredClone(doc)));
  }
}
